import { api } from "@/lib/api";

const KEY = "gs_offline_queue";
const DRAFT = "gs_offline_drafts";

const read = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k) || "[]");
  } catch (e) {
    return [];
  }
};

export const pendingCount = () => read(KEY).length;

export const isOffline = () => !navigator.onLine;

export const enqueue = (method, url, body) => {
  const queue = read(KEY);
  queue.push({ id: `${Date.now()}-${Math.random()}`, method, url, body });
  localStorage.setItem(KEY, JSON.stringify(queue));
  window.dispatchEvent(new Event("gs-queue-change"));
};

export const saveDraft = (id, data) => {
  const drafts = JSON.parse(localStorage.getItem(DRAFT) || "{}");
  drafts[id] = data;
  localStorage.setItem(DRAFT, JSON.stringify(drafts));
};

export const getDraft = (id) => {
  const drafts = JSON.parse(localStorage.getItem(DRAFT) || "{}");
  return drafts[id];
};

export const clearDraft = (id) => {
  const drafts = JSON.parse(localStorage.getItem(DRAFT) || "{}");
  delete drafts[id];
  localStorage.setItem(DRAFT, JSON.stringify(drafts));
};

export const cacheAgenda = (day, data) =>
  localStorage.setItem(`gs_agenda_${day}`, JSON.stringify(data));

export const readAgendaCache = (day) => {
  try {
    return JSON.parse(localStorage.getItem(`gs_agenda_${day}`) || "null");
  } catch (e) {
    return null;
  }
};

export const enqueuePhoto = async (interventionId, phase, file) => {
  const dataUrl = await compressImage(file);
  const queue = read(KEY);
  queue.push({
    id: `${Date.now()}-${Math.random()}`,
    type: "photo",
    interventionId,
    phase,
    filename: file.name || "foto.jpg",
    dataUrl,
  });
  localStorage.setItem(KEY, JSON.stringify(queue));
  window.dispatchEvent(new Event("gs-queue-change"));
};

export const pendingPhotos = (interventionId) =>
  read(KEY).filter((i) => i.type === "photo" && i.interventionId === interventionId);

/** Ridimensiona la foto a max 1280px lato lungo per stare nello storage locale. */
export const compressImage = (file, max = 1280, quality = 0.72) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const dataUrlToBlob = (dataUrl) => {
  const [meta, b64] = dataUrl.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

export const flushQueue = async () => {
  if (!navigator.onLine) return { sent: 0, failed: 0 };
  const queue = read(KEY);
  const remaining = [];
  let sent = 0;
  for (const item of queue) {
    try {
      if (item.type === "photo") {
        const fd = new FormData();
        fd.append("file", dataUrlToBlob(item.dataUrl), item.filename.replace(/\.[^.]+$/, "") + ".jpg");
        fd.append("phase", item.phase);
        await api.post(`/interventions/${item.interventionId}/photos`, fd);
      } else {
        await api.request({ method: item.method, url: item.url, data: item.body });
      }
      sent += 1;
    } catch (e) {
      if (!e.response) remaining.push(item); // errore di rete: riprova più tardi
      // errori 4xx: la richiesta non è ripetibile, la scartiamo
    }
  }
  localStorage.setItem(KEY, JSON.stringify(remaining));
  window.dispatchEvent(new Event("gs-queue-change"));
  return { sent, failed: remaining.length };
};

/** Salva subito se online, altrimenti mette in coda e restituisce queued=true. */
export const sendOrQueue = async (method, url, body) => {
  if (!navigator.onLine) {
    enqueue(method, url, body);
    return { queued: true };
  }
  try {
    const { data } = await api.request({ method, url, data: body });
    return { queued: false, data };
  } catch (e) {
    if (!e.response) {
      enqueue(method, url, body);
      return { queued: true };
    }
    throw e;
  }
};
